import { Component, forwardRef, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../services/api.service';

@Component({
  standalone: true,
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrl: './address.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressComponent),
      multi: true
    }
  ]
})
export class AddressComponent implements ControlValueAccessor, OnInit {
  @Input() addressType: 'vendor' | 'customer' = 'vendor'; // Default to vendor for backward compatibility
  @Input() readonly: boolean = false;
  
  addressForm: FormGroup;
  private dataAlreadyProvided = false; // Track if data was provided by parent form

  // Address types loaded from API
  addressTypes: any[] = [];

  // ControlValueAccessor callbacks
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService
  ) {
    this.addressForm = this.fb.group({
      id: [0],
      addressType: [null],
      addressLine1: [''],
      addressLine2: [''],
      addressLine3: [''],
      addressLine4: [''],
      addressLine5: [''],
      city: [''],
      state: [''],
      postalCode: [''],
      country: ['']
    });

    // Subscribe to form value changes and propagate to parent
    this.addressForm.valueChanges.subscribe(value => {
      this.onChange(value);
      this.onTouched();
    });
  }

  ngOnInit(): void {
    // Load address types from API
    this.loadAddressTypes();

    // Detect address type from URL if not explicitly set
    const currentUrl = this.router.url;
    if (currentUrl.includes('/customers')) {
      this.addressType = 'customer';
    } else if (currentUrl.includes('/vendors')) {
      this.addressType = 'vendor';
    }
    
    // Check if an ID route parameter is supplied
    const id = this.route.snapshot.paramMap.get('id');
    
    // Defer the API call to allow writeValue to run first
    // This prevents redundant API calls when data is already provided by parent
    if (id) {
      setTimeout(() => {
        if (!this.dataAlreadyProvided) {
          this.loadAddress(id);
        }
      }, 0);
    }
  }

  loadAddressTypes(): void {
    this.apiService.get<any[]>('address/externalTypes').subscribe({
      next: (data) => {
        this.addressTypes = data || [];
      },
      error: (err) => {
        console.error('Error loading address types:', err);
        this.addressTypes = [];
      }
    });
  }

  loadAddress(id: string): void {
    // Use the appropriate endpoint based on address type
    const endpoint = `address/${this.addressType}/${id}`;
    
    this.apiService.get<any>(endpoint).subscribe({
      next: (data) => {
        if (data) {
          const address = {
            addressType: data.addressType || data.AddressType || null,
            addressLine1: data.addressLine1 || data.AddressLine1 || '',
            addressLine2: data.addressLine2 || data.AddressLine2 || '',
            addressLine3: data.addressLine3 || data.AddressLine3 || '',
            addressLine4: data.addressLine4 || data.AddressLine4 || '',
            addressLine5: data.addressLine5 || data.AddressLine5 || '',
            city: data.city || data.City || '',
            state: data.state || data.State || '',
            postalCode: data.postalCode || data.PostalCode || '',
            country: data.country || data.Country || ''
          };
          this.addressForm.patchValue(address);
        }
      },
      error: (error) => {
        console.error('Error loading address:', error);
      }
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    // Always mark as provided — if writeValue is called at all, the parent controls this data
    this.dataAlreadyProvided = true;
    if (value) {
      // Normalize id in case the API returned PascalCase (Id) instead of camelCase (id)
      const normalized = { ...value, id: value.id ?? value.Id ?? 0 };
      this.addressForm.patchValue(normalized, { emitEvent: false });
    } else {
      this.addressForm.reset({ id: 0 }, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.addressForm.disable();
    } else {
      this.addressForm.enable();
    }
  }
}
